import { Command } from 'commander';
import path from 'path';
import z from 'zod';
import { parse as parseYaml } from 'yaml';
import api from '../services/api';
import models from '../models';
import file from '../utils/file';
import asyncErrorWrapper from '../utils/async-error-wrapper';
import { displayTestResults } from '../utils/test-result-formatter';
import output from '../utils/output-writer';

const testActionInputSchema = models.tool.testActionCommandInput;

const testCaseSchema = z.union([
  z.object({
    alertLink: z.string(),
    expectation: z.string(),
  }),
  z.object({
    expectation: z.string(),
    title: z.string(),
    time: z.string().datetime().optional(),
    attributes: z.record(z.string()).optional(),
    details: z.string().optional(),
  }),
]);

const testConfigSchema = z.object({
  testCases: z.array(testCaseSchema).min(1),
});

function toInputList(testCases: z.infer<typeof testConfigSchema>['testCases']) {
  return testCases.map((testCase) =>
    'alertLink' in testCase
      ? { messageLink: testCase.alertLink, expectation: testCase.expectation }
      : testCase
  );
}

const testAction = new Command('test-action')
  .description('Run playbook for alert links')
  .requiredOption(
    '-a, --action-title <actionTitle>',
    testActionInputSchema.shape.actionTitle.description
  )
  .option(
    '-b, --book-path <bookFilePath>',
    testActionInputSchema.shape.bookPath.description
  )
  .requiredOption(
    '-t, --test-path <testFilePath>',
    testActionInputSchema.shape.testPath.description
  )
  .option(
    '-o, --output-file <outputFilePath>',
    testActionInputSchema.shape.outputFile.description
  )
  .action(
    asyncErrorWrapper(async (options) => {
      const parsedTestActionInput = testActionInputSchema.safeParse(options);
      if (!parsedTestActionInput.success) {
        output.info(JSON.stringify(parsedTestActionInput.error, null, 2));
        return;
      }
      const { bookPath, testPath, actionTitle, outputFile } =
        parsedTestActionInput.data;

      const testFileStr = await file.readFile(testPath);
      const parsedTestConfig = testConfigSchema.safeParse(
        parseYaml(testFileStr)
      );
      if (!parsedTestConfig.success) {
        console.error(parsedTestConfig.error.message);
        return;
      }

      const inputList = toInputList(parsedTestConfig.data.testCases);
      const bookYamlStr = bookPath && (await file.readFile(bookPath));
      const bookName = bookPath && path.basename(bookPath, '.yaml');

      const results = await api.testAction({
        bookYamlStr,
        bookName,
        inputList,
        actionTitle,
      });

      await displayTestResults(results, actionTitle, {
        outputFile,
      });
    })
  );

export default testAction;
